import { BaseObject } from './types'

/**
 * Status of an AI Coach session
 */
export type AICoachSessionStatus =
  | 'available' // Session is available, waiting for participants
  | 'ai_speaking' // AI is currently speaking
  | 'paused' // AI has finished one segment, waiting for next
  | 'completed' // All segments completed
  | 'error' // Error occurred
  | 'pregenerating' // AI content is being pregenerated

export const AI_COACH_INTRO_PART = 0 as const
export const AI_COACH_CONTENT_PARTS = [1, 2, 3] as const
export const AI_COACH_PARTS = [AI_COACH_INTRO_PART, ...AI_COACH_CONTENT_PARTS] as const
export const AI_COACH_LAST_PART = 3 as const

/**
 * AI Coach starts with a static intro, followed by 3 generated parts/segments.
 */
export type AICoachPart = (typeof AI_COACH_PARTS)[number]

/**
 * Generated content for all 3 parts of an AI Coach session
 */
export interface AICoachContent {
  intro?: string // Static introduction to the AI debrief flow
  segment1?: string // Task performance analysis
  segment2?: string // Communication & communicative space
  segment3?: string // Taking it into everyday work
  language?: string
  generatedAt?: string
}

/**
 * Generated audio assets for the AI Coach content.
 * Stored as public URLs (Firebase Storage objects made public).
 */
export interface AICoachTtsAudio {
  introUrl?: string
  segment1Url?: string
  segment2Url?: string
  segment3Url?: string
  introTextHash?: string
  segment1TextHash?: string
  segment2TextHash?: string
  segment3TextHash?: string
  audioEncoding?: 'MP3' | 'LINEAR16'
  sampleRateHz?: number
  languageCode?: string
  voiceName?: string
  generatedAt?: string
}

/**
 * A queued playback task that a separate long-running service can execute.
 * That service is responsible for joining the Whereby room and injecting the audio.
 */
export interface AICoachAssistantTask {
  taskId: string
  part: AICoachPart
  audioUrl: string
  status: 'queued' | 'running' | 'completed' | 'failed'
  createdAt: string
  updatedAt?: string
  attemptCount?: number
  lastError?: string
}

/**
 * A queued pregeneration task for AI Coach content (LLM + TTS).
 * This is written to the AI Coach session document and picked up by a Firestore trigger.
 */
export interface AICoachPregenerationTask {
  taskId: string
  gameSessionId: string // The game session ID to use for getting loop phases data
  status: 'queued' | 'running' | 'completed' | 'failed'
  createdAt: string
  updatedAt?: string
  attemptCount?: number
  lastError?: string
  /** An operator's forced restart: every part is written again, the old text kept until the new is ready. */
  isRewrite?: boolean
}

/**
 * AI Coach session stored in Firestore
 */
export interface AICoachSession extends BaseObject {
  // Identity
  activityId: string
  sprintId: string
  teamId: string

  // State
  status: AICoachSessionStatus
  currentPart: AICoachPart | null
  /**
   * Tracks which parts have been fully completed (AI finished speaking).
   * This makes it clear which parts are done vs which is in progress.
   */
  completedParts: AICoachPart[]

  // Whereby Room Info (captured when session is created to ensure all participants and AI use the same room)
  /**
   * The specific Whereby room URL for this session.
   * Stored at session creation time to prevent race conditions where users and AI assistant
   * could end up in different rooms if a new room is created during the session.
   */
  wherebyRoomUrl?: string
  /**
   * The host room URL (with elevated permissions) for the AI assistant to use.
   */
  wherebyHostRoomUrl?: string

  // Participants
  participants: string[] // every member of the team, not only the ones who joined

  // AI Content (cached)
  aiContent?: AICoachContent

  // TTS Audio (cached)
  ttsAudio?: AICoachTtsAudio

  // Assistant playback task (consumed by external service)
  assistantTask?: AICoachAssistantTask

  // Pregeneration task (consumed by Firestore trigger)
  pregenerationTask?: AICoachPregenerationTask

  // Timestamps
  aiTriggeredAt?: string
  completedAt?: string
  // Set each time status becomes 'pregenerating'; used by the scheduled reaper to detect stranded sessions.
  pregeneratingStartedAt?: string
  // A facilitator or operator ended the debrief for the team: every open debrief page leaves on this stamp.
  forceEndedAt?: string
  forceEndedByUserId?: string

  // Error handling
  lastError?: string
}

/**
 * Response from getAICoachSession
 */
export interface GetAICoachSessionResponse {
  success: boolean
  session?: AICoachSessionData
  error?: string
}

/**
 * AI Coach session data returned to frontend
 */
export interface AICoachSessionData {
  sessionId: string
  activityId: string
  sprintId: string
  teamId: string
  status: AICoachSessionStatus
  currentPart: AICoachPart | null
  /**
   * Tracks which parts have been fully completed (AI finished speaking).
   * This makes it clear which parts are done vs which is in progress.
   */
  completedParts: AICoachPart[]
  /**
   * The specific Whereby room URL for this session.
   * Frontend should use this instead of fetching from team data to ensure
   * all participants and AI use the same room.
   */
  wherebyRoomUrl?: string
  participants: string[]
  /** The names those participants go by, by user id. */
  participantNames?: Record<string, string>
  aiContent?: AICoachContent
  ttsAudio?: AICoachTtsAudio
  assistantTask?: AICoachAssistantTask
  /** A facilitator or operator ended the debrief for the team; the page leaves as soon as it sees this. */
  forceEndedAt?: string
}

/**
 * Request to trigger AI Coach to speak
 */
export interface TriggerAICoachRequest {
  sessionId: string
  part: AICoachPart
}

/**
 * Response from triggerAICoach
 */
export interface TriggerAICoachResponse {
  success: boolean
  error?: string
}

/** Why a press to start or replay a part was refused. */
export enum AICoachTriggerErrorCode {
  SESSION_NOT_FOUND = 'session-not-found',
  NOT_AUTHORIZED_FOR_TEAM = 'not-authorized-for-team',
  AI_ALREADY_SPEAKING = 'ai-already-speaking',
  PART_ALREADY_COMPLETED = 'part-already-completed',
  INVALID_PART_PROGRESSION = 'invalid-part-progression',
  CONTENT_GENERATION_FAILED = 'content-generation-failed',
  NOTHING_TO_REPLAY = 'nothing-to-replay',
  AUDIO_NOT_AVAILABLE = 'audio-not-available'
}

/**
 * Request to replay the AI Coach audio for the current part.
 *
 * Replay re-triggers the AI assistant to join the Whereby room and speak the
 * current part again for EVERYONE (the assistant streams into the shared room).
 * It reuses the cached TTS audio and does NOT advance the session or touch
 * completed parts.
 */
export interface ReplayAICoachRequest {
  sessionId: string
}

/**
 * Data needed to generate AI Coach content
 */
export interface AICoachGenerationData {
  teamScoresNormalised: Record<string, number>
  teamScoresRaw: Record<string, number>
  teamSessionTranscript: string
}

export interface AICoachIntroAsset extends BaseObject {
  languageCode: string
  voiceName: string
  /** How it was spoken, so a tuning change re-renders the intro as well as the voice name does. */
  voiceTuningKey?: string
  text: string
  textHash: string
  audioUrl?: string
  audioEncoding?: 'MP3' | 'LINEAR16'
  sampleRateHz?: number
  generatedAt?: string
  updatedAt?: string
  updatedBy?: string
  lastError?: string
}
